import t from "@ext/localization/locale/translate";
import type { DiffChange } from "@gaurussel/tiptap-diff-utility";
import type { Node } from "prosemirror-model";
import { Plugin, PluginKey } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

export const inlineComponentChangesKey = new PluginKey<DecorationSet>("diff-inline-component-changes");

const COMPONENT = "inlineMd_component";

export interface ComponentChange {
	from: number;
	to: number;
	oldText: string | null;
}

const isComponent = (node: Node) => node.type.name === COMPONENT;

const componentsOf = (node: Node): { node: Node; pos: number }[] => {
	const found: { node: Node; pos: number }[] = [];
	node.descendants((child, pos) => {
		if (isComponent(child)) found.push({ node: child, pos });
		return !child.isInline;
	});
	return found;
};

const componentCount = (node: Node) => componentsOf(node).length;

const sameComponent = (a: Node, b: Node) =>
	a.attrs.text === b.attrs.text && JSON.stringify(a.attrs.tag) === JSON.stringify(b.attrs.tag);

const tagText = (tag: unknown): string | null => {
	const first = Array.isArray(tag) ? tag[0] : null;
	const attributes = first && typeof first === "object" ? (first as { attributes?: unknown }).attributes : null;
	if (!attributes || typeof attributes !== "object") return null;
	const value = Object.values(attributes).find((item) => typeof item === "string" && item);
	return (value as string) ?? null;
};

export const previousText = (node: Node): string | null =>
	(typeof node.attrs.text === "string" && node.attrs.text) || tagText(node.attrs.tag);

const atomChange = (change: DiffChange, doc: Node): ComponentChange | null => {
	const posB = change.posB as number;
	if (posB < 0 || posB >= doc.content.size) return null;
	if (componentCount(doc.resolve(posB).parent) !== 1) return null;
	return { from: posB, to: posB + (change.nodeB as Node).nodeSize, oldText: previousText(change.nodeA as Node) };
};

const blockChange = (change: DiffChange): ComponentChange | null => {
	const before = componentsOf(change.nodeA as Node);
	const after = componentsOf(change.nodeB as Node);
	if (before.length !== 1 || after.length !== 1) return null;
	if (sameComponent(before[0].node, after[0].node)) return null;
	const from = (change.posB as number) + 1 + after[0].pos;
	return { from, to: from + after[0].node.nodeSize, oldText: previousText(before[0].node) };
};

export const componentChanges = (changes: DiffChange[], doc: Node): ComponentChange[] => {
	const result = new Map<number, ComponentChange>();
	for (const change of changes) {
		if (change.type !== "modified" || !change.nodeA || !change.nodeB || change.posB === undefined) continue;
		const found = isComponent(change.nodeB as Node) ? atomChange(change, doc) : blockChange(change);
		if (found) result.set(found.from, found);
	}
	return [...result.values()];
};

const decorationsFor = (changes: DiffChange[], doc: Node) => {
	const size = doc.content.size;
	const decorations = componentChanges(changes, doc)
		.filter(({ from, to }) => from >= 0 && to <= size && from < to)
		.map(({ from, to, oldText }) =>
			Decoration.node(from, to, {
				class: "diff-inline-changed",
				...(oldText ? { title: `${t("diff.inline.was")}: ${oldText}` } : {}),
			}),
		);
	return decorations.length ? DecorationSet.create(doc, decorations) : DecorationSet.empty;
};

const inlineComponentChanges = (getChanges: () => DiffChange[] | undefined) =>
	new Plugin<DecorationSet>({
		key: inlineComponentChangesKey,
		state: {
			init: (_, state) => decorationsFor(getChanges() ?? [], state.doc),
			apply: (tr, set) =>
				tr.getMeta(inlineComponentChangesKey)
					? decorationsFor(getChanges() ?? [], tr.doc)
					: set.map(tr.mapping, tr.doc),
		},
		props: {
			decorations(state) {
				return inlineComponentChangesKey.getState(state);
			},
		},
	});

export default inlineComponentChanges;
